import { ReactNode } from "react";
import { useTranslations } from "use-intl";
import {
  RejectExternalSigningMutationVariables,
  RejectSigningMutationVariables,
} from "gql/graphql";
import { FormInModal } from "@/shared/form/form-in-modal";
import Form from "@/shared/form/form";
import { TextArea } from "@/shared/form/input/textarea";
import { SubmitButton, SubmitWrapper } from "@/shared/form/button";
import { REJECTION_COMMENT_MAX_LENGTH } from "./model/constants";

/** Значения формы — общее поле `comment` обеих мутаций отказа. */
type RejectionValues = Pick<
  RejectSigningMutationVariables | RejectExternalSigningMutationVariables,
  "comment"
>;

/**
 * Модал отказа от подписания с необязательным комментарием. Сам отказ не выполняет —
 * его мутация у вызывающего (внутренний и внешний подписант отказываются разными),
 * оттуда же и `loading`. Остальные переменные мутации (кто отказывается) добавляет вызывающий.
 */
export function RejectSigningModal({
  trigger,
  onSubmit,
  loading,
}: {
  trigger: (open: () => void) => ReactNode;
  /** Модал закрывается, только если отказ прошёл */
  onSubmit: (values: RejectionValues) => Promise<boolean>;
  loading: boolean;
}) {
  const t = useTranslations("RejectSigning");

  return (
    <FormInModal
      title={t("Reject signing title")}
      trigger={trigger}
      form={(close) => (
        <Form<RejectionValues>
          disabled={loading}
          onFinish={async (values) => {
            const rejected = await onSubmit(values);

            if (rejected) {
              close();
            }
          }}
        >
          <Form.Item<RejectionValues>
            name="comment"
            label={t("Comment")}
            extra={t("Comment hint")}
          >
            <TextArea
              data-testid={"reject-signing-comment"}
              withoutAI
              maxLength={REJECTION_COMMENT_MAX_LENGTH}
              showCount
              autoSize={{ minRows: 3 }}
            />
          </Form.Item>

          <SubmitWrapper>
            <SubmitButton danger loading={loading}>
              {t("Reject")}
            </SubmitButton>
          </SubmitWrapper>
        </Form>
      )}
    />
  );
}
